import React from 'react';
import { BubbleData } from './useMapData';
import { SiteData } from '../../types';

interface MapTooltipProps {
  hoveredBubble: BubbleData | null;
  hovered: any;
  mousePos: { x: number; y: number };
  width: number;
  onCountryClick?: (countryName: string) => void;
}

export const MapTooltip: React.FC<MapTooltipProps> = ({ 
  hoveredBubble, 
  hovered, 
  mousePos, 
  width,
  onCountryClick
}) => {
  return (
    <>
      {/* Modern Bubble Tooltip */}
      {hoveredBubble && (
        <div
          className="absolute pointer-events-none z-30 animate-fade-in"
          style={{
            left: Math.min(mousePos.x + 16, width - 280),
            top: Math.max(mousePos.y - 80, 12),
          }}
        >
          <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl p-4 shadow-2xl min-w-[250px]">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-3 h-3 rounded-full ${hoveredBubble.isHealthy ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <h3 className="font-semibold text-foreground text-base">{hoveredBubble.location}</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Active Sites</span>
                <span className="font-medium text-foreground">{hoveredBubble.activeSites}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Sites</span>
                <span className="font-medium text-foreground">{hoveredBubble.totalSites}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Health Ratio</span>
                <span className={`font-medium ${hoveredBubble.isHealthy ? 'text-green-600' : 'text-red-600'}`}>
                  {((hoveredBubble.activeSites / hoveredBubble.totalSites) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Sessions Last Month</span>
                <span className="font-medium text-foreground">{hoveredBubble.sessionsLastMonth.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Sessions (12 Months)</span>
                <span className="font-medium text-foreground">{hoveredBubble.sessions12Months.toLocaleString()}</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
              Active = Online + {'>'}10 sessions (12 months)
            </div>
          </div>
        </div>
      )}

      {/* Clickable Country Tooltip */}
      {hovered && !hoveredBubble && (
        <button
          className="absolute z-20 animate-fade-in cursor-pointer hover:scale-105 transition-transform"
          style={{
            left: 16,
            top: 12,
            padding: "8px 12px",
            fontFamily: "Inter, ui-sans-serif, system-ui, Segoe UI, Roboto, Helvetica, Arial",
            fontSize: 13,
            color: "#ffffff",
            background: "rgba(22, 52, 106, 0.95)",
            border: "1px solid rgba(255,255,255,.2)",
            borderRadius: 8,
            backdropFilter: "blur(6px)",
            boxShadow: "0 4px 18px rgba(0,0,0,.25)",
          }}
          onClick={() => onCountryClick?.(hovered.properties?.name || "Unknown")}
        >
          <div className="flex items-center gap-2">
            <span>{hovered.properties?.name || "Unknown"}</span>
            <span className="text-xs opacity-75">Click for details</span>
          </div>
        </button>
      )}
    </>
  );
};